import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { KeyStatus } from "../api/types";

interface Props {
  entry: KeyStatus;
}

/** One row in the API-keys list — shows masked status + an expandable edit form. */
export function ApiKeyRow({ entry }: Props) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const save = useMutation({
    mutationFn: (v: string) => api.settings.putKey(entry.name, v),
    onSuccess: () => {
      setEditing(false);
      setValue("");
      void qc.invalidateQueries({ queryKey: ["api_keys"] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    save.mutate(value);
  };

  const onClear = () => {
    if (confirm(`${entry.label} wirklich löschen?`)) {
      save.mutate("");
    }
  };

  return (
    <div className="rounded-xl border border-black/5 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div
          className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${
            entry.configured ? "bg-emerald-500" : "bg-red-400"
          }`}
          aria-hidden
        />
        <div className="flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-brand-text-dark">
                {entry.label}
              </div>
              <div className="font-mono text-[11px] text-brand-text-dark/50">
                {entry.name}
              </div>
            </div>
            <div className="text-right">
              {entry.configured ? (
                <code className="rounded bg-emerald-50 px-2 py-0.5 font-mono text-xs text-emerald-800">
                  {entry.masked ?? "set"}
                </code>
              ) : (
                <code className="rounded bg-red-50 px-2 py-0.5 font-mono text-xs text-red-800">
                  not set
                </code>
              )}
            </div>
          </div>
          {entry.hint ? (
            <p className="mt-1.5 text-xs text-brand-text-dark/60">{entry.hint}</p>
          ) : null}

          {!editing ? (
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setEditing(true)}
                className="rounded-full bg-brand-blue px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-cream hover:bg-brand-blue-deep"
              >
                {entry.configured ? "Ändern" : "Hinzufügen"}
              </button>
              {entry.configured ? (
                <button
                  onClick={onClear}
                  disabled={save.isPending}
                  className="rounded-full px-3.5 py-1.5 text-[11px] uppercase tracking-wider text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Löschen
                </button>
              ) : null}
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-3 flex flex-wrap gap-2">
              <input
                autoFocus
                type={entry.secret ? "password" : "text"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={entry.secret ? "neuen Wert einfügen" : "Wert"}
                className="flex-1 min-w-[200px] rounded-lg border border-black/10 bg-brand-beige px-3 py-2 font-mono text-xs focus:border-brand-blue focus:outline-none"
              />
              <button
                type="submit"
                disabled={save.isPending || !value}
                className="rounded-full bg-emerald-600 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {save.isPending ? "…" : "Speichern"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setValue("");
                }}
                className="rounded-full px-4 py-2 text-[11px] uppercase tracking-wider text-brand-text-dark/60 hover:bg-black/5"
              >
                Abbrechen
              </button>
            </form>
          )}
          {save.error ? (
            <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-900">
              {String(save.error)}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
